import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Background, Handle, MarkerType, Position, ReactFlow, type Node } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api, ApiRequestError } from "../api/client";
import { useLegalOs, type LegalOsBundle, type LegalOsKeyword, type LegalOsPage } from "../hooks/useLegalOs";
import { PAGE_PREVIEW_PATTERNS } from "../../shared/pagePreviewPatterns";
import { WizardSteps } from "../components/WizardSteps";
import catAiAvatar from "../assets/generated/cat-ai-avatar.png";

const defaultOutline: Array<[string, string]> = [
    ["最初の見出し", "事務所名・代表写真・登録情報を出し、安心して相談できる印象を作ります。"],
    ["信頼できる理由", "行政書士本人の経験、対応地域、相談しやすさを整理します。"],
    ["料金と流れ", "相談前に知りたい費用感と、手続き完了までの流れを示します。"],
    ["相談事例", "近い状況の相談者が、自分ごととして読める事例を入れます。"],
    ["お問い合わせ", "電話・フォームなど、相談しやすい導線へ案内します。"],
  ];

const patternOutlines: Record<string, Array<[string, string]>> = {
  trust: defaultOutline,
  problem: [
    ["よくある悩み", "検索者が言葉にしにくい不安を、最初に代弁します。"],
    ["放置した時の困りごと", "書類不備、期限、許可の見込みなどの不安を整理します。"],
    ["解決の進め方", "行政書士に相談すると何が楽になるかを具体化します。"],
    ["相談前の確認事項", "料金・必要書類・対応範囲を、やさしい言葉で確認できます。"],
    ["お問い合わせ", "不安が残る方がすぐ相談できる導線を置きます。"],
  ],
  corporate: [
    ["法人向け見出し", "スピード、正確性、継続支援を最初に伝えます。"],
    ["対応できる業務範囲", "新規申請、更新、変更届、周辺手続きまで整理します。"],
    ["必要書類と進行管理", "担当者が社内で準備しやすいように、必要な動きを示します。"],
    ["料金・納期・相談方法", "比較検討しやすい情報を、ページ上部から確認できます。"],
    ["法人向けCTA", "電話・フォームで相談しやすい導線を強めます。"],
  ],
  authority: [
    ["実績と専門性", "許可取得件数、難案件、法人対応の実績を前面に出します。"],
    ["対応できる理由", "専門知識・チーム体制・周辺業務への対応力を整理します。"],
    ["難しい案件の例", "相談者が自分の状況に近いと感じるケースを掲載します。"],
    ["料金と相談方法", "専門性に見合う費用と相談導線を明確にします。"],
  ],
  price: [
    ["料金表", "基本報酬・実費・追加料金条件を比較しやすく示します。"],
    ["納期と必要書類", "依頼前に必要な情報を短く整理します。"],
    ["申込みの流れ", "すぐ依頼できる導線を中心に構成します。"],
  ],
  local: [
    ["地域の手続き概要", "対象地域・管轄・地域固有の注意点を見せます。"],
    ["近隣地域への導線", "関連する地域ページへ自然につなげます。"],
    ["地域の相談事例", "その地域で相談しやすい印象を作ります。"],
  ],
  familiar: [
    ["相談しやすい入口", "作り込みすぎない素朴な見た目で依頼のハードルを下げます。"],
    ["地域密着の安心感", "地元の言葉と写真を使い、近い存在として伝えます。"],
    ["やさしい料金・流れ", "専門用語を避けて短く説明します。"],
  ],
  message_story: [
    ["代表メッセージ", "事務所の想いを長めのストーリーで読ませます。"],
    ["相談者への約束", "何を大切に対応するかを文章で伝えます。"],
    ["解決までの物語", "相談前から解決後までの流れを読み物として見せます。"],
  ],
  manga: [
    ["漫画の導入", "相談者キャラクターの悩みから入ります。"],
    ["先生の解説", "吹き出しと図解で手続きを説明します。"],
    ["相談への誘導", "漫画の最後に診断・相談へつなげます。"],
  ],
  review: [
    ["お客様の声", "相談前の悩み、依頼理由、結果を掲載します。"],
    ["許可取得事例", "地域・業種・課題・対応・結果で信頼を作ります。"],
    ["同じ悩みの方へ", "近い状況の人が相談しやすい導線を置きます。"],
  ],
  hub: [
    ["手続き別の入口", "関連する専門ページや記事へ分岐させます。"],
    ["ケース別導線", "相談者の状況に応じて必要ページへ案内します。"],
    ["内部リンク", "SEO資産として下層ページを育てます。"],
  ],
  diagnosis: [
    ["無料診断", "自分のケースで進められるか確認できる導線を置きます。"],
    ["要件チェック", "必要条件をチェックリスト形式で見せます。"],
    ["相談予約", "診断後に自然に問い合わせへ進めます。"],
  ],
};

function SiteGraphNode({ data }: { data: { label: string; status?: string; statusTone?: string; firstViewImageUrl?: string | null } }) {
  return (
    <div className={`lp-flow-node-label ${data.firstViewImageUrl ? "with-preview" : ""} ${data.statusTone === "published" ? "published" : ""}`}>
      <Handle type="target" id="top" position={Position.Top} />
      <Handle type="target" id="left" position={Position.Left} />
      <Handle type="source" id="bottom" position={Position.Bottom} />
      <Handle type="source" id="right" position={Position.Right} />
      {data.firstViewImageUrl && (
        <span className="lp-node-first-view" aria-hidden="true">
          <img src={data.firstViewImageUrl} alt="" />
        </span>
      )}
      <strong>{data.label}</strong>
      {data.status && <span className={`lp-node-status ${data.statusTone ?? ""}`}>{data.status}</span>}
    </div>
  );
}

const nodeTypes = { siteGraphNode: SiteGraphNode };


type ViewMode = "map" | "detail";
type RevisionMessage = { role: "user" | "assistant"; text: string; ready?: boolean };
type HearingSummary = {
  pricing_note?: string;
  fee_table_text?: string;
  testimonial_text?: string;
  services?: Array<{ price_mode?: string; price_rows?: unknown[] }>;
};

function hasHearingFeeInfo(hearing: HearingSummary | null): boolean {
  if (!hearing) return false;
  if (String(hearing.pricing_note ?? "").trim()) return true;
  if (String(hearing.fee_table_text ?? "").trim()) return true;
  return Array.isArray(hearing.services) && hearing.services.some((service) => {
    if (String(service.price_mode ?? "").trim()) return true;
    return Array.isArray(service.price_rows) && service.price_rows.length > 0;
  });
}

function hasHearingProofInfo(hearing: HearingSummary | null): boolean {
  if (!hearing) return false;
  return Boolean(String(hearing.testimonial_text ?? "").trim());
}

function revisionReady(messages: RevisionMessage[], known: { fee?: boolean; proof?: boolean } = {}): boolean {
  const assistantReady = messages.some((message) => message.role === "assistant" && message.ready);
  const text = messages.map((message) => message.text).join("\n");
  const feeConfirmed = Boolean(known.fee) || /(料金|費用|報酬|見積|税込|税別|非掲載|未定)/.test(text);
  const proofConfirmed = Boolean(known.proof) || /(実績|事例|お客様の声|口コミ|許可取得|掲載許可|匿名|なし|まだない)/.test(text);
  return assistantReady && feeConfirmed && proofConfirmed;
}

function statusLabel(status: string): string {
  if (status === "approved") return "作成中";
  if (status === "in_production") return "作成中";
  if (status === "rejected") return "拒否";
  if (status === "draft") return "確認待ち";
  if (status === "deferred") return "保留";
  if (status === "planned") return "作成予定";
  if (status === "published") return "公開中";
  return "作成予定";
}

function statusClass(status: string): string {
  if (status === "published") return "published";
  if (status === "approved" || status === "in_production") return "green";
  if (status === "rejected") return "red";
  if (status === "draft") return "amber";
  return "";
}

function pageStatus(page?: LegalOsPage | null): string {
  return page?.effective_status ?? page?.status ?? "planned";
}

function nodeBackgroundFor(status: string): string {
  if (status === "published") return "#ebf8f0";
  if (status === "approved" || status === "in_production") return "#f2fbf6";
  if (status === "draft") return "#fff8e8";
  if (status === "rejected") return "#fff1f1";
  return "#ffffff";
}

function nodeBorderFor(status: string): string {
  if (status === "published") return "#66c98f";
  if (status === "approved" || status === "in_production") return "#bde9d0";
  if (status === "draft") return "#ffd493";
  if (status === "rejected") return "#ffc9c9";
  return "#dbe4f3";
}

// ノードの実高さを概算する。react-flow のカスタムノードは padding を持たず 1px ボーダーのみで、
// 中身の .lp-flow-node-label は grid + gap。プレビュー付き(.with-preview)は 16:9 画像の分だけ背が高い。
// この高さを行間(レベル間)の縦マージン計算に反映し、プレビュー付きでも縦に被らないようにする。
function estimateNodeHeight(nodeWidth: number, hasPreview: boolean, labelLines: number, fontSize: number): number {
  const border = 2; // 上下 1px ずつ
  const gap = hasPreview ? 9 : 8; // .lp-flow-node-label(.with-preview) の gap
  const previewHeight = hasPreview ? Math.round(((nodeWidth - border) * 9) / 16) : 0; // .lp-node-first-view aspect-ratio 16/9
  const labelHeight = Math.max(1, labelLines) * Math.ceil(fontSize * 1.45); // line-height 1.45
  const statusHeight = 30; // .lp-node-status: min-height 24 + padding + border
  const parts = [previewHeight, labelHeight, statusHeight].filter((height) => height > 0);
  const gaps = Math.max(0, parts.length - 1) * gap;
  return border + previewHeight + labelHeight + statusHeight + gaps;
}

function compactKeyword(keyword?: string | null): string {
  return (keyword ?? "").replace(/\s+/g, " ").trim();
}

function supportKeywordsFor(keyword?: LegalOsKeyword): string[] {
  const text = compactKeyword(keyword?.keyword);
  if (!text) return [];
  const tokens = text.split(" ").filter(Boolean);
  const area = tokens.find((token) => /市|区|町|村|県|全域|全国/.test(token)) ?? "";
  const service = tokens.find((token) => !/市|区|町|村|県|全域|全国|行政書士|相談|代行|費用|料金|無料相談/.test(token)) ?? "";
  const candidates = [
    area && service ? `${area} ${service} 料金` : "",
    area && service ? `${area} ${service} 相談` : "",
    service ? `${service} 必要書類` : "",
    service ? `${service} 自分でできる` : "",
  ].filter(Boolean);
  return Array.from(new Set(candidates)).filter((item) => item !== text).slice(0, 4);
}

function subKeywordsForPage(keywords: LegalOsKeyword[], mainKeyword?: LegalOsKeyword): string[] {
  if (!mainKeyword?.target_page_id) return [];
  const samePageKeywords = keywords
    .filter((keyword) => keyword.target_page_id === mainKeyword.target_page_id && keyword.id !== mainKeyword.id)
    .sort((a, b) => b.priority_score - a.priority_score)
    .map((keyword) => keyword.keyword);
  const generated = samePageKeywords.length === 0 ? supportKeywordsFor(mainKeyword) : [];
  return Array.from(new Set([...samePageKeywords, ...generated]));
}

function folderLabel(keyword?: string | null, fallback?: string | null): string {
  const value = compactKeyword(keyword);
  if (!value) return `${fallback ?? "専門ページ"}といえば`;
  const tokens = value.split(" ").filter(Boolean);
  const area = (tokens[0] ?? "").replace(/全域$/, "");
  const service = tokens
    .slice(1)
    .filter((token) => !["行政書士", "相談", "代行", "費用", "料金", "無料相談"].includes(token))
    .join("");
  if (area && service) return `${area}の${service}といえば`;
  return `${value}といえば`;
}

function patternKey(preview?: { pattern: string; skill_name: string } | null): string {
  if (!preview) return "trust";
  if (preview.pattern in patternOutlines) return preview.pattern;
  if (PAGE_PREVIEW_PATTERNS.some((pattern) => pattern.pattern === preview.pattern)) return preview.pattern;
  if (preview.skill_name.includes("不安")) return "problem";
  if (preview.skill_name.includes("法人")) return "corporate";
  return "trust";
}

function roleForPage(page: LegalOsPage, keyword?: LegalOsKeyword): string {
  if (page.page_type === "home") return "各専門ページへつなぐ入口ページ";
  if (keyword?.primary_intent?.includes("情報") || keyword?.recommended_page_type?.includes("記事")) {
    return "調べ始めた方の不安を解消するページ";
  }
  if (keyword?.primary_intent?.includes("直前") || keyword?.keyword?.includes("料金") || keyword?.keyword?.includes("無料相談")) {
    return "問い合わせ直前の方を相談につなげるページ";
  }
  return "検索から相談につなげる専門ページ";
}

function customerForPage(page: LegalOsPage, keyword?: LegalOsKeyword): string {
  const target = keyword?.keyword ? `「${keyword.keyword}」で検索している方` : page.h1 ?? page.title;
  if (page.page_type === "home") return "事務所の対応業務や先生の雰囲気を知りたい方";
  return `${target}。料金・流れ・依頼先選びに不安がある方。`;
}

export function PagePlanPage() {
  const { caseRow, os, setOs, loading } = useLegalOs();
  const pages = os?.pages ?? [];
  const keywords = os?.keywords ?? [];
  const [view, setView] = useState<ViewMode>("map");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewActionBusy, setPreviewActionBusy] = useState(false);
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);
  const [siteBuildNotice, setSiteBuildNotice] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revisionInput, setRevisionInput] = useState("");
  const [revisionBusy, setRevisionBusy] = useState(false);
  const [revisionMessagesByPage, setRevisionMessagesByPage] = useState<Record<string, RevisionMessage[]>>({});
  const [hearing, setHearing] = useState<HearingSummary | null>(null);
  const revisionThreadRef = useRef<HTMLDivElement | null>(null);
  const [previews, setPreviews] = useState<Array<{
    id: string;
    pattern: string;
    skill_name: string;
    status: string;
    selected: number;
    error: string | null;
    direction?: string;
    required_info?: string[];
    sensitive_info?: string[];
    requirement_summary?: string;
    images: Array<{ id: string; segment: string; image_url: string; sort_order: number }>;
  }>>([]);
  const [error, setError] = useState<string | null>(null);
  const autoGenerated = useRef(false);

  const keywordByPage = useMemo(() => {
    const map = new Map<string, LegalOsKeyword>();
    const keywordById = new Map(keywords.map((keyword) => [keyword.id, keyword]));
    for (const page of pages) {
      if (!page.target_keyword_id) continue;
      const keyword = keywordById.get(page.target_keyword_id);
      if (keyword) map.set(page.id, keyword);
    }
    for (const keyword of keywords) {
      if (!keyword.target_page_id) continue;
      const current = map.get(keyword.target_page_id);
      const preferred = Boolean(keyword.recommended_page_type?.includes("地域×業務") || keyword.recommended_page_type?.includes("トップ"));
      const currentPreferred = Boolean(current?.recommended_page_type?.includes("地域×業務") || current?.recommended_page_type?.includes("トップ"));
      if (!current || (preferred && !currentPreferred)) map.set(keyword.target_page_id, keyword);
    }
    return map;
  }, [keywords, pages]);

  const homePage = useMemo(() => pages.find((p) => p.page_type === "home" || p.url_path === "/") ?? pages[0] ?? null, [pages]);
  const specialistPages = useMemo(() => pages.filter((p) => p.id !== homePage?.id), [pages, homePage?.id]);
  const groups = useMemo(() => specialistPages.map((page) => {
    const keyword = keywordByPage.get(page.id);
    return {
      key: page.id,
      label: folderLabel(keyword?.keyword, page.title),
      keyword,
      page,
      role: roleForPage(page, keyword),
    };
  }), [specialistPages, keywordByPage]);
  const selected = useMemo(() => pages.find((p) => p.id === selectedId) ?? homePage, [pages, selectedId, homePage]);
  const selectedKeyword = selected ? keywordByPage.get(selected.id) : undefined;
  const selectedIsMainPage = Boolean(selected && (selected.page_type === "home" || selected.target_keyword_id));
  const selectedSubKeywords = useMemo(() => {
    return subKeywordsForPage(keywords, selectedKeyword);
  }, [keywords, selectedKeyword]);
  const selectedGenerating = previews.some((preview) => preview.status === "generating");
  const activePreview = previews.find((preview) => preview.id === activePreviewId)
    ?? previews.find((preview) => preview.selected)
    ?? previews.find((preview) => preview.status === "ready")
    ?? previews[0]
    ?? null;
  const activeOutline = patternOutlines[patternKey(activePreview)] ?? defaultOutline;
  const revisionMessages = selected?.id ? revisionMessagesByPage[selected.id] ?? [] : [];
  const knownSensitiveInfo = useMemo(() => ({
    fee: hasHearingFeeInfo(hearing),
    proof: hasHearingProofInfo(hearing),
  }), [hearing]);
  const hasReadyPreview = previews.some((preview) => preview.status === "ready" && preview.images.length > 0);
  const visibleRevisionMessages = revisionMessages;
  const hasPageInfoAnswer = hasReadyPreview || revisionReady(revisionMessages, knownSensitiveInfo);
  const selectedStatus = pageStatus(selected);
  const pageInProduction = selectedStatus === "approved" || selectedStatus === "in_production" || selectedStatus === "published";
  const decided = pageInProduction;
  const hasFailedPreview = previews.some((preview) => preview.status === "failed");

  const regenerate = async () => {
    if (!caseRow) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/strategy/generate`, {});
      setOs(res.os);
      setSelectedId(res.os.pages[0]?.id ?? null);
      setView("map");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "ページ設計の生成に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (loading || !caseRow || pages.length > 0 || keywords.length === 0 || autoGenerated.current) return;
    autoGenerated.current = true;
    void regenerate();
  }, [loading, caseRow?.id, pages.length, keywords.length]);

  useEffect(() => {
    if (!caseRow) return;
    api.get<{ hearing: HearingSummary }>(`/api/cases/${caseRow.id}/hearing`)
      .then((res) => setHearing(res.hearing ?? null))
      .catch(() => setHearing(null));
  }, [caseRow?.id]);

  const loadPreviews = async (pageId: string) => {
    if (!caseRow) return [];
    const res = await api.get<{ previews: typeof previews }>(`/api/cases/${caseRow.id}/pages/${pageId}/previews`);
    setPreviews(res.previews);
    setActivePreviewId((current) => {
      if (current && res.previews.some((preview) => preview.id === current)) return current;
      return res.previews.find((preview) => preview.selected)?.id ?? res.previews.find((preview) => preview.status === "ready")?.id ?? res.previews[0]?.id ?? null;
    });
    return res.previews;
  };

  const loadRevisionMessages = async (pageId: string) => {
    if (!caseRow) return;
    try {
      const res = await api.get<{ messages: RevisionMessage[] }>(`/api/cases/${caseRow.id}/pages/${pageId}/revision-chat`);
      setRevisionMessagesByPage((all) => ({
        ...all,
        [pageId]: res.messages.map((message) => ({
          role: message.role,
          text: message.text,
          ready: message.ready,
        })),
      }));
    } catch {
      // チャット履歴の復元に失敗しても、ページ設計自体は続けられる。
    }
  };

  const updateStatus = async (status: "planned" | "approved" | "draft" | "deferred") => {
    if (!caseRow || !selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ os: LegalOsBundle; preview_generation?: string; site_build_request?: string }>(`/api/cases/${caseRow.id}/pages/${selected.id}`, { status });
      setOs(res.os);
      if (status === "approved" && res.preview_generation === "queued") {
        await loadPreviews(selected.id);
      }
      if (res.site_build_request === "created") {
        setSiteBuildNotice(true);
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "ページ状態の更新に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const generatePreviews = async (retryFailed = false, messagesOverride?: RevisionMessage[]) => {
    if (!caseRow || !selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/cases/${caseRow.id}/pages/${selected.id}/previews`, {
        revision_messages: messagesOverride ?? revisionMessages,
        retry_failed: retryFailed,
      });
      await loadPreviews(selected.id);
      setRevisionOpen(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "画面イメージの生成に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const selectPreview = async (previewId: string) => {
    if (!caseRow) return;
    setPreviewActionBusy(true);
    setError(null);
    try {
      await api.patch(`/api/cases/${caseRow.id}/page-previews/${previewId}`, {});
      if (selected?.id) await loadPreviews(selected.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "画面イメージの選択に失敗しました");
    } finally {
      setPreviewActionBusy(false);
    }
  };

  const decideDesign = async () => {
    if (!activePreview || activePreview.status !== "ready") return;
    await selectPreview(activePreview.id);
    await updateStatus("approved");
    setConfirmOpen(false);
    setView("map");
  };

  const addRevisionMessage = async () => {
    const text = revisionInput.trim();
    if (!text || !selected?.id || !caseRow) return;
    const nextMessages: RevisionMessage[] = [...revisionMessages, { role: "user", text }];
    setRevisionMessagesByPage((all) => ({ ...all, [selected.id]: nextMessages }));
    setRevisionInput("");
    setRevisionBusy(true);
    setError(null);
    try {
      const res = await api.post<{ reply: string; ready: boolean }>(
        `/api/cases/${caseRow.id}/pages/${selected.id}/revision-chat`,
        {
          messages: nextMessages,
          has_ready_preview: hasReadyPreview,
        },
      );
      const finalMessages: RevisionMessage[] = [...nextMessages, { role: "assistant", text: res.reply, ready: res.ready }];
      setRevisionMessagesByPage((all) => ({
        ...all,
        [selected.id]: finalMessages,
      }));
      if (res.ready && !hasReadyPreview) {
        await generatePreviews(false, finalMessages);
      }
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.message : "ページ設計チャットに失敗しました";
      setRevisionMessagesByPage((all) => ({
        ...all,
        [selected.id]: [...nextMessages, { role: "assistant", text: message }],
      }));
    } finally {
      setRevisionBusy(false);
    }
  };

  const openRevisionChat = async () => {
    setRevisionOpen(true);
    if (!selected?.id || !caseRow || revisionBusy) return;
    const existing = revisionMessagesByPage[selected.id] ?? [];
    if (existing.length > 0) return;
    setRevisionBusy(true);
    setError(null);
    try {
      const res = await api.post<{ reply: string; ready: boolean }>(
        `/api/cases/${caseRow.id}/pages/${selected.id}/revision-chat`,
        {
          messages: [],
          initial: true,
          has_ready_preview: hasReadyPreview,
        },
      );
      const finalMessages: RevisionMessage[] = [{ role: "assistant", text: res.reply, ready: res.ready }];
      setRevisionMessagesByPage((all) => ({
        ...all,
        [selected.id]: finalMessages,
      }));
      if (res.ready && !hasReadyPreview) {
        await generatePreviews(false, finalMessages);
      }
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.message : "最初の質問を作成できませんでした。少し時間を置いてもう一度開いてください。";
      setRevisionMessagesByPage((all) => ({
        ...all,
        [selected.id]: [{ role: "assistant", text: message }],
      }));
    } finally {
      setRevisionBusy(false);
    }
  };

  useEffect(() => {
    if (caseRow && selected?.id) {
      void loadPreviews(selected.id);
      void loadRevisionMessages(selected.id);
    }
  }, [caseRow?.id, selected?.id]);

  useEffect(() => {
    if (!revisionOpen) return;
    const el = revisionThreadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [revisionOpen, visibleRevisionMessages.length, revisionBusy]);

  useEffect(() => {
    if (!caseRow || !selected?.id) return;
    const hasGenerating = previews.some((preview) => preview.status === "generating");
    if (!hasGenerating) return;
    const timer = window.setInterval(() => {
      void loadPreviews(selected.id);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [caseRow?.id, selected?.id, previews]);

  const openDetail = (pageId: string) => {
    setSelectedId(pageId);
    setView("detail");
  };

  const deletePage = async () => {
    if (!caseRow || !selected || selected.id === homePage?.id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.delete<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/pages/${selected.id}`);
      setOs(res.os);
      setSelectedId(res.os.pages.find((page) => page.page_type === "home")?.id ?? res.os.pages[0]?.id ?? null);
      setView("map");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "ページ削除に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  // 行(レベル)ごとの Y 座標を、その行の最も背が高いノード(プレビュー付き)を基準に決める。
  // 固定値の y=40/260/420 だとプレビュー付きノードが下の行に被るため、ノード高＋余白で動的に算出する。
  const graphLayout = useMemo(() => {
    const folderWidth = groups.length > 5 ? 210 : 260;
    const subKeywordWidth = 178;
    const spacing = Math.max(folderWidth + 520, 720);
    const startX = 40;
    const groupNodeWidth = Math.max(folderWidth, 250);
    const rowGap = 80; // 行間の余白

    const homeY = 40;
    const homeHeight = homePage ? estimateNodeHeight(280, Boolean(homePage.first_view_image_url), 3, 15) : 0;
    const contactHeight = estimateNodeHeight(260, false, 2, 15); // お問い合わせノードも上段に並ぶ
    const topRowHeight = Math.max(homeHeight, contactHeight);

    const groupY = homeY + topRowHeight + rowGap;
    const groupMaxHeight = groups.length
      ? Math.max(
          ...groups.map((group) =>
            estimateNodeHeight(groupNodeWidth, Boolean(group.page.first_view_image_url), 2, 15),
          ),
        )
      : 0;

    const hasSubkeywords = groups.some((group) => subKeywordsForPage(keywords, group.keyword).length > 0);
    const subkeywordY = groupY + groupMaxHeight + rowGap;
    const subkeywordHeight = estimateNodeHeight(subKeywordWidth, false, 2, 12);
    const bottomY = hasSubkeywords ? subkeywordY + subkeywordHeight : groupY + groupMaxHeight;
    const height = Math.max(720, Math.round(bottomY + 48)); // 下端に余白を足したコンテナ高

    return { folderWidth, subKeywordWidth, spacing, startX, groupNodeWidth, homeY, groupY, subkeywordY, height };
  }, [groups, homePage, keywords]);

  const graphNodes = useMemo(() => {
    const nodes = [];
    const { subKeywordWidth, spacing, startX, groupNodeWidth, homeY, groupY, subkeywordY } = graphLayout;
    if (homePage) {
      const status = pageStatus(homePage);
      nodes.push({
        id: homePage.id,
        type: "siteGraphNode",
        position: { x: 80, y: homeY },
        data: {
          label: `トップページ\n${homePage.title}`,
          status: statusLabel(status),
          statusTone: statusClass(status),
          firstViewImageUrl: homePage.first_view_image_url,
        },
        style: {
          width: 280,
          border: `1px solid ${nodeBorderFor(status)}`,
          borderRadius: 8,
          background: nodeBackgroundFor(status),
          color: "#071638",
          fontWeight: 800,
          whiteSpace: "pre-line",
        },
      });
    }
    groups.forEach((group, index) => {
      const x = startX + index * spacing;
      const status = pageStatus(group.page);
      nodes.push({
        id: group.key,
        type: "siteGraphNode",
        position: { x, y: groupY },
        data: {
          label: group.label,
          status: statusLabel(status),
          statusTone: statusClass(status),
          firstViewImageUrl: group.page.first_view_image_url,
        },
        style: {
          width: groupNodeWidth,
          border: `1px solid ${nodeBorderFor(status)}`,
          borderRadius: 8,
          background: nodeBackgroundFor(status),
          color: "#071638",
          fontWeight: 800,
          whiteSpace: "pre-line",
        },
      });
      subKeywordsForPage(keywords, group.keyword).forEach((keyword, itemIndex) => {
        nodes.push({
          id: `${group.key}-subkeyword-${itemIndex}`,
          type: "siteGraphNode",
          position: { x: x + itemIndex * (subKeywordWidth + 14), y: subkeywordY },
          data: { label: `サブキーワード\n${keyword}`, status: "ページ内で対策", statusTone: "" },
          style: { width: subKeywordWidth, border: "1px solid #e2e8f0", borderRadius: 8, background: "#ffffff", color: "#475569", fontWeight: 800, whiteSpace: "pre-line", fontSize: 12 },
        });
      });
    });
    nodes.push({
      id: "contact-page",
      type: "siteGraphNode",
      position: { x: 420, y: graphLayout.homeY },
      data: { label: "お問い合わせ\n相談導線の共通ページ", status: "共通導線", statusTone: "" },
      style: { width: 260, border: "1px solid #dbe4f3", borderRadius: 8, background: "#f8fbff", color: "#33415f", fontWeight: 800, whiteSpace: "pre-line" },
    });
    return nodes;
  }, [homePage, groups, keywords, graphLayout]);

  const graphWidth = useMemo(() => {
    const folderWidth = groups.length > 5 ? 210 : 260;
    const spacing = Math.max(folderWidth + 520, 720);
    const groupWidth = groups.length > 0 ? 40 + (groups.length - 1) * spacing + folderWidth + 820 : 0;
    return Math.max(1440, groupWidth);
  }, [groups.length]);

  const graphEdges = useMemo(() => {
    const edges = [];
    if (homePage) {
      for (const group of groups) {
        edges.push({
          id: `${homePage.id}-${group.key}`,
          source: homePage.id,
          sourceHandle: "bottom",
          target: group.key,
          targetHandle: "top",
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: "#9db8ff", strokeWidth: 2 },
        });
        subKeywordsForPage(keywords, group.keyword).forEach((_, index) => {
          edges.push({
            id: `${group.key}-subkeyword-${index}`,
            source: group.key,
            sourceHandle: "bottom",
            target: `${group.key}-subkeyword-${index}`,
            targetHandle: "top",
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#cbd8f6", strokeWidth: 1.4 },
          });
        });
      }
      edges.push({
        id: `${homePage.id}-contact`,
        source: homePage.id,
        sourceHandle: "right",
        target: "contact-page",
        targetHandle: "left",
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { stroke: "#cbd8f6", strokeWidth: 1.5 },
      });
    }
    return edges;
  }, [homePage, groups, keywords]);

  if (loading) return <div className="page-center">読み込み中...</div>;

  if (pages.length === 0) {
    return (
      <section className="lp-page">
        <WizardSteps current="pagePlan" />
        <div className="lp-page-head">
          <div>
            <span className="lp-section-kicker">PAGE PLAN</span>
            <h1>ページ設計</h1>
            <p>素材管理まで終わると、狙うキーワードごとのページ構成を確認できます。</p>
          </div>
          <Link className="btn btn-ghost" to="/assets">素材管理へ</Link>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="lp-info-banner">
          <strong>ページ設計はまだありません。</strong>
          <p>{keywords.length > 0 ? "採用キーワードをもとに、ページ設計をAIで生成できます。" : "ヒアリングとキーワード戦略が完了すると、ページ設計へ進めます。"}</p>
          {keywords.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => void regenerate()} disabled={busy}>
              {busy ? "生成中..." : "ページ設計をAIで生成"}
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="lp-page lp-page-map">
      {error && <div className="alert alert-error">{error}</div>}
      <WizardSteps current={pages.some((page) => ["approved", "in_production", "published"].includes(pageStatus(page))) ? "approval" : "pagePlan"} />
      {view === "map" && (
        <div className="lp-page-head">
          <div>
            <span className="lp-section-kicker">PAGE PLAN</span>
            <h1>ページ設計</h1>
            <p>サイト全体の構造を確認してから、各ページの内容を承認します。</p>
          </div>
          <div className="lp-inline-actions">
            {selectedGenerating ? (
              <button className="btn btn-ghost" disabled>素材管理へ戻る</button>
            ) : (
              <Link className="btn btn-ghost" to="/assets">素材管理へ戻る</Link>
            )}
          </div>
        </div>
      )}

      {view === "map" && (
        <div className="lp-sitemap-stage">
          <div className="lp-sitemap-intro">
            <h2>あなたのホームページは、以下の構成で作成されます。</h2>
            <p>グラフ上のノードをクリックすると、ページの役割と完成イメージを確認できます。</p>
          </div>
          <div className="lp-site-graph" style={{ width: graphWidth, height: graphLayout.height }} aria-label="サイト全体マップ">
            <ReactFlow
              nodes={graphNodes}
              edges={graphEdges}
              nodeTypes={nodeTypes}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              nodesDraggable={false}
              nodesConnectable={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              panOnDrag={false}
              panOnScroll={false}
              preventScrolling={false}
              proOptions={{ hideAttribution: true }}
              elementsSelectable
              onNodeClick={(_: React.MouseEvent, node: Node) => {
                if (node.id === "contact-page") return;
                if (node.id.includes("-subkeyword-")) return;
                openDetail(node.id);
              }}
            >
              <Background gap={24} size={1} color="#dbe4f3" />
            </ReactFlow>
          </div>
        </div>
      )}

      {view === "detail" && selected && (
        <div className="lp-page-detail-simple">
          <button className="lp-back-link" onClick={() => setView("map")}>← サイト全体マップへ戻る</button>
          <button className="lp-chat-slider-tab" type="button" onClick={() => void openRevisionChat()}>
            チャットで回答する
          </button>
          <div className="lp-detail-hero">
            <div>
              <span className="lp-section-kicker">{selected.page_type === "home" ? "TOP PAGE" : "PAGE DETAIL"}</span>
              <h1>{selected.page_type === "home" ? "トップページ" : folderLabel(selectedKeyword?.keyword, selected.title)}{selected.page_type === "home" ? "" : "（メインページ）"}</h1>
              <p>{selected.h1 ?? selected.title}</p>
              {selectedKeyword && <small>キーワードフォルダ：{folderLabel(selectedKeyword.keyword, selected.title)}</small>}
            </div>
            <div className="lp-detail-status">
              <span>狙うキーワード</span>
              <strong>{selectedKeyword?.keyword ?? "事務所名・対応業務"}</strong>
              <em className={`lp-status-pill ${statusClass(selectedStatus)}`}>{statusLabel(selectedStatus)}</em>
              {selected.public_url && (
                <a className="btn btn-primary btn-sm lp-public-page-link" href={selected.public_url} target="_blank" rel="noreferrer">
                  公開ページを開く
                </a>
              )}
            </div>
          </div>

          {(selectedStatus === "approved" || selectedStatus === "in_production" || selectedStatus === "published") && (
            <div className="lp-engineering-banner in-detail">
              <strong>{selectedStatus === "published" ? "このページは公開中です" : siteBuildNotice ? "現在エンジニアがサイトを作成中です" : "このページのデザインは決定済みです"}</strong>
              <p>{selectedStatus === "published"
                ? "ページ内容を確認する場合は、公開ページへのリンクから実際のサイトを開けます。"
                : siteBuildNotice
                ? "すべてのページが決定または保留になったため、ローカルPC上の制作環境で実サイトを実装しています。"
                : "他のページも個別に確認し、すべて決定または保留になるとエンジニアへ制作依頼が送られます。"}</p>
            </div>
          )}

          <div className="lp-detail-layout">
            <div className="lp-detail-summary">
              <div className="lp-card lp-purpose-card">
                <span className="lp-target-icon">◎</span>
                <h2>このページの目的</h2>
                <p>{customerForPage(selected, selectedKeyword)}</p>
              </div>
              {selectedKeyword && (
                <div className="lp-card lp-page-keyword-card">
                  <h2>このページで使うキーワード</h2>
                  <div className="lp-main-keyword">
                    <span>メインキーワード</span>
                    <strong>{selectedKeyword.keyword}</strong>
                  </div>
                  {selectedSubKeywords.length > 0 ? (
                    <div className="lp-subkeyword-tree in-detail">
                      <strong>サブキーワード</strong>
                      <ul>
                        {selectedSubKeywords.map((keyword) => (
                          <li key={keyword}>{keyword}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="muted small">このページには、まだサブキーワードが設定されていません。</p>
                  )}
                </div>
              )}
              <div className="lp-card lp-outline-card">
                <h2>入る内容</h2>
                {hasReadyPreview ? (
                  <>
                    <p className="muted small">選択中のデザイン方針に合わせて、ページに入る内容も変わります。</p>
                    <ol>
                      {activeOutline.map(([title, text], index) => (
                        <li key={title}>
                          <span>{index + 1}</span>
                          <div>
                            <strong>{title}</strong>
                            <small>{text}</small>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </>
                ) : (
                  <div className="lp-needed-info">
                    <p>画面イメージ生成後に、確定した内容を表示します。</p>
                    <strong>生成前にAIチャットで確認します</strong>
                    {!hasPageInfoAnswer && (
                      <small>AIがページの目的・キーワード・事務所情報を見ながら、必要な情報を対話で確認します。</small>
                    )}
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => void openRevisionChat()}>
                      AIチャットで回答する
                    </button>
                  </div>
                )}
              </div>
              <div className="lp-card lp-source-card">
                <h2>生成に使う情報</h2>
                <p>ヒアリング内容、採用キーワード、素材管理の写真・ロゴ・料金表・お客様の声を入力として使います。</p>
                <Link className="btn btn-ghost btn-sm" to="/assets">素材を確認する</Link>
              </div>
            </div>

            <div className="lp-card lp-preview-focus">
              <div className="lp-section-head">
                <div>
                  <h2>完成イメージ</h2>
                  <p className="muted">3種類のデザイン方針から選びます。方針を切り替えると、左の「入る内容」も切り替わります。</p>
                </div>
                {selectedGenerating && <span className="lp-status-pill">生成中</span>}
              </div>
              {previews.length > 0 && (
                <div className="lp-preview-choice-row top">
                  {previews.map((preview) => {
                    const isActive = activePreview?.id === preview.id;
                    const isSelected = decided && Boolean(preview.selected);
                    const disabled = previewActionBusy || decided || preview.status === "generating";
                    return (
                      <button
                        key={preview.id}
                        className={`${isActive ? "selected" : ""} ${isSelected ? "chosen" : ""} ${decided && !preview.selected ? "locked" : ""}`}
                        disabled={disabled}
                        onClick={() => {
                          setActivePreviewId(preview.id);
                          if (preview.status === "ready") void selectPreview(preview.id);
                        }}
                      >
                        <strong>{preview.skill_name}</strong>
                        <span>
                          {isSelected
                            ? "選択中"
                            : decided
                              ? "作成中"
                              : preview.status === "ready"
                                ? "クリックして選択"
                                : preview.status === "failed"
                                  ? "失敗"
                                  : preview.status === "planned"
                                    ? "生成前の候補"
                                    : "生成中"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {!activePreview ? (
                <div className="lp-preview-empty">
                  <strong>まだ画面イメージはありません。</strong>
                  <p>3種類のデザイン案を生成すると、ここに実際の画像プレビューが表示されます。</p>
                </div>
              ) : (
                <div className="lp-long-preview hero-preview">
                  {activePreview.status === "planned" && (
                    <div className="lp-preview-empty">
                      <strong>まだ画面イメージはありません。</strong>
                      <p>必要な情報をチャットで確認してから、デザイン案を生成します。</p>
                    </div>
                  )}
                  {activePreview.images.map((img) => (
                    <figure key={img.id}>
                      <img src={img.image_url} alt={`${activePreview.skill_name} ${img.segment}`} />
                      <figcaption>{img.segment}</figcaption>
                    </figure>
                  ))}
                  {activePreview.status === "generating" && <p className="muted">画像生成中です。この画面を離れてもタスクは継続します。</p>}
                  {activePreview.status === "failed" && (
                    <div className="alert alert-error">
                      <p>{activePreview.error ?? "生成に失敗しました"}</p>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => void generatePreviews(true)} disabled={busy}>
                        リロード
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="lp-detail-actions">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => {
                    if (hasReadyPreview) {
                      setConfirmOpen(true);
                      return;
                    }
                    if (!hasPageInfoAnswer) {
                      void openRevisionChat();
                      return;
                    }
                    void generatePreviews(false);
                  }}
                  disabled={busy || previewActionBusy || selectedGenerating || decided || (hasFailedPreview && !hasReadyPreview)}
                >
                  {decided
                    ? "決定済み"
                    : selectedGenerating
                      ? "デザイン案を生成中..."
                      : hasReadyPreview
                        ? "このデザインで決定"
                        : hasFailedPreview
                          ? "リロードしてください"
                          : hasPageInfoAnswer
                            ? "3種類のデザイン案を生成"
                            : "追加質問に回答して下さい"}
                </button>
                <button className="btn btn-ghost btn-lg" onClick={() => void openRevisionChat()} disabled={busy || pageInProduction}>修正したい</button>
                {!selectedIsMainPage && (
                  <>
                    <button className="btn btn-ghost btn-lg" onClick={() => void updateStatus("deferred")} disabled={busy}>保留</button>
                    <button className="btn btn-ghost btn-lg danger" onClick={() => void deletePage()} disabled={busy}>削除</button>
                  </>
                )}
              </div>
            </div>
          </div>
          {confirmOpen && (
            <div className="lp-modal-backdrop" role="presentation" onMouseDown={() => setConfirmOpen(false)}>
              <div className="lp-modal lp-confirm-modal" role="dialog" aria-modal="true" aria-label="デザイン決定の確認" onMouseDown={(e) => e.stopPropagation()}>
                <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setConfirmOpen(false)}>×</button>
                <span className="lp-section-kicker">CONFIRM</span>
                <h2>本当に決定してよろしいですか？</h2>
                <p>このデザインで制作依頼を出すと、初回フローでは変更できません。運営側の制作管理に反映され、エンジニアがサイト制作を開始します。</p>
                <div className="lp-modal-actions">
                  <button className="btn btn-ghost" type="button" onClick={() => setConfirmOpen(false)}>戻る</button>
                  <button className="btn btn-primary" type="button" onClick={() => void decideDesign()} disabled={busy || previewActionBusy}>
                    このデザインで決定
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className={`lp-chat-drawer-layer ${revisionOpen ? "open" : ""}`} aria-hidden={!revisionOpen}>
            <button className="lp-chat-drawer-scrim" type="button" aria-label="閉じる" onClick={() => setRevisionOpen(false)} />
            <div className="lp-chat-drawer" role="dialog" aria-modal="true" aria-label="ページ修正の相談">
              <div className="lp-chat-drawer-head">
                <div>
                  <span className="lp-section-kicker">PAGE CHAT</span>
                  <h2>{hasReadyPreview ? "どこを修正したいですか？" : "このページに必要な情報を教えてください"}</h2>
                </div>
                <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setRevisionOpen(false)}>×</button>
              </div>
              <p className="muted">{hasReadyPreview
                ? "雰囲気、色、写真の使い方、見出し、信頼感、漫画風にしたいなど、自然な言葉で入力してください。"
              : "AIが会話しながら、このページを作るために必要な情報を確認します。まずは分かる範囲で自然に入力してください。"}</p>
              <div className="lp-revision-thread" ref={revisionThreadRef}>
                {visibleRevisionMessages.length === 0 ? (
                  <div className="lp-revision-row assistant">
                    <span className="lp-revision-avatar">
                      <img src={catAiAvatar} alt="" />
                    </span>
                    <div className="lp-revision-message assistant">
                      {revisionBusy ? "このページに合わせた最初の質問を準備しています..." : "チャットを開始します。"}
                    </div>
                  </div>
                ) : (
                  visibleRevisionMessages.map((message, index) => (
                    <div key={`${message.role}-${index}`} className={`lp-revision-row ${message.role}`}>
                      {message.role === "assistant" && (
                        <span className="lp-revision-avatar">
                          <img src={catAiAvatar} alt="" />
                        </span>
                      )}
                      <div className={`lp-revision-message ${message.role}`}>
                        {message.text}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="lp-revision-input">
                <textarea value={revisionInput} onChange={(e) => setRevisionInput(e.target.value)} rows={4} placeholder={hasReadyPreview ? "修正したい内容を入力" : "例）料金表はまだ未定です。対応地域は福岡市と久留米市、建設業許可は福岡県全域です。相談事例は建設会社の新規許可が1件あります。"} />
                <button className="btn btn-ghost" type="button" onClick={() => void addRevisionMessage()} disabled={revisionBusy || !revisionInput.trim()}>
                  {revisionBusy ? "確認中..." : "送信"}
                </button>
              </div>
              <div className="lp-drawer-actions">
                <button className="btn btn-primary" type="button" onClick={() => void generatePreviews()} disabled={busy || selectedGenerating || !hasPageInfoAnswer}>
                  {hasReadyPreview ? "この内容で画像を再生成" : "この内容でデザイン案を生成"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
